import { useState, useEffect } from "react";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  updateUserProfile,
  getUsers,
  moveAgentCoordinatorTasks,
  getAlldealforAdmin,
} from "../../utils/firebasefunctions";
import { findAgentCoordinatorTasks } from "../../utils/taskSections";

const isStaff = (u) =>
  (u.role === "Employee" || u.role === "Admin" || u.role === "Supervisor") &&
  u.type !== 1 &&
  u.type !== 2;

// One tab for both coordinator roles. Listing Coordinators handle "LC:" template
// tasks; Transaction Coordinators handle "TC:" tasks. Each agent gets one of
// each, and those auto-tasks assign to the matching coordinator when a deal's
// tasks are built.
const CoordinatorsTab = () => {
  const [{ users, deals }, dispatch] = useStateValue();
  const [coverage, setCoverage] = useState(null);

  const staff = (users || []).filter(isStaff);
  const listingCoordinators = staff.filter((u) => u.isListingCoordinator);
  const transactionCoordinators = staff.filter((u) => u.isTransactionCoordinator);
  const agents = staff.filter(
    (u) => !u.isListingCoordinator && !u.isTransactionCoordinator,
  );

  // Ensure deals are loaded so the LC reassignment prompt can count open tasks.
  useEffect(() => {
    if (!deals || deals.length === 0) {
      getAlldealforAdmin("deals").then((res) => {
        if (Array.isArray(res))
          dispatch({ type: actionType.STORE_DEALS, deals: res });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUsers = async () => {
    const fresh = await getUsers();
    dispatch({ type: actionType.STORE_USERS, payload: fresh });
  };

  const toggleListing = async (user) => {
    await updateUserProfile(user.id, {
      isListingCoordinator: !user.isListingCoordinator,
    });
    await refreshUsers();
  };

  const toggleTransaction = async (user) => {
    await updateUserProfile(user.id, {
      isTransactionCoordinator: !user.isTransactionCoordinator,
    });
    await refreshUsers();
  };

  const assignListing = async (agent, newCoordId) => {
    const oldCoordId = agent.listingCoordinatorId || null;
    await updateUserProfile(agent.id, {
      listingCoordinatorId: newCoordId || null,
    });
    if (oldCoordId && oldCoordId !== newCoordId) {
      const matches = findAgentCoordinatorTasks(deals || [], agent.id, oldCoordId);
      if (matches.length > 0) {
        const newCoord = listingCoordinators.find((c) => c.id === newCoordId);
        setCoverage({ agent, oldCoordId, newCoord, count: matches.length });
      }
    }
    await refreshUsers();
  };

  const assignTransaction = async (agent, newCoordId) => {
    await updateUserProfile(agent.id, {
      transactionCoordinatorId: newCoordId || null,
    });
    await refreshUsers();
  };

  const confirmMove = async () => {
    const { agent, oldCoordId, newCoord } = coverage;
    const min = newCoord
      ? {
          id: newCoord.id,
          firstname: newCoord.firstname,
          lastname: newCoord.lastname,
          profilepicture: newCoord.profilepicture || "",
          role: newCoord.role,
        }
      : null;
    await moveAgentCoordinatorTasks(deals || [], agent.id, oldCoordId, min);
    setCoverage(null);
  };

  const pill = (active, activeClass) =>
    `text-xs px-2.5 py-1 rounded-full border ${
      active ? activeClass : "bg-white text-gray-600 border-gray-300"
    }`;

  return (
    <div className="p-4">
      <h3 className="text-lg font-bold text-[#1E293B] mb-1">Coordinators</h3>
      <p className="text-xs text-gray-500 mb-5">
        Listing Coordinators handle "LC:" template tasks; Transaction
        Coordinators handle "TC:" tasks. Each agent's tasks auto-assign to their
        matching coordinator.
      </p>

      {/* Who is a coordinator — Listing and/or Transaction, per person */}
      <div className="mb-6">
        <p className="text-sm font-semibold text-gray-600 mb-2">
          Who Is a Coordinator
        </p>
        <div className="space-y-1.5">
          {staff.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between gap-3 py-1"
            >
              <span className="text-sm text-gray-800">
                {u.firstname} {u.lastname}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleListing(u)}
                  className={pill(
                    u.isListingCoordinator,
                    "bg-[#1E293B] text-white border-[#1E293B]",
                  )}
                >
                  Listing
                </button>
                <button
                  onClick={() => toggleTransaction(u)}
                  className={pill(
                    u.isTransactionCoordinator,
                    "bg-emerald-700 text-white border-emerald-700",
                  )}
                >
                  Transaction
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Each agent's coordinators — a Listing and a Transaction dropdown */}
      <div>
        <p className="text-sm font-semibold text-gray-600 mb-2">
          Each Agent's Coordinators
        </p>
        <div className="divide-y">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
            >
              <span className="text-sm text-gray-800 flex-1 min-w-[120px]">
                {agent.firstname} {agent.lastname}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400">Listing</span>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={agent.listingCoordinatorId || ""}
                  onChange={(e) => assignListing(agent, e.target.value)}
                >
                  <option value="">(none)</option>
                  {listingCoordinators.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.firstname} {c.lastname}
                    </option>
                  ))}
                </select>
                <span className="text-[11px] text-gray-400">Transaction</span>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={agent.transactionCoordinatorId || ""}
                  onChange={(e) => assignTransaction(agent, e.target.value)}
                >
                  <option value="">(none)</option>
                  {transactionCoordinators.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.firstname} {c.lastname}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {coverage && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm">
            <p className="text-sm text-gray-800 mb-4">
              {coverage.agent.firstname} {coverage.agent.lastname} has{" "}
              {coverage.count} open task{coverage.count === 1 ? "" : "s"} on the
              previous Listing Coordinator. Also move{" "}
              {coverage.count === 1 ? "it" : "them"} to{" "}
              {coverage.newCoord
                ? `${coverage.newCoord.firstname} ${coverage.newCoord.lastname}`
                : "the new coordinator"}
              ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-sm rounded border"
                onClick={() => setCoverage(null)}
              >
                Leave Them
              </button>
              <button
                className="px-3 py-1.5 text-sm rounded bg-[#1E293B] text-white"
                onClick={confirmMove}
              >
                Move Them
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoordinatorsTab;
